import type { FormItemProps, InputNumberProps } from "antd";
import { Form, InputNumber } from "antd";
import React, { ReactNode } from "react";
import type { ControllerProps, FieldValues, Path } from "react-hook-form";
import { Controller } from "react-hook-form";
import { getRequiredErrorMessage } from "../../libs/form/utils/getRequiredErrorMessage";
import { renderExtra } from "./renderExtra";

export type InputNumberFieldProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues>> = {
  fieldname: ControllerProps<TFieldValues, TName>["name"];
  label?: FormItemProps["label"];
  control: ControllerProps<TFieldValues, TName>["control"];
  rules?: ControllerProps<TFieldValues, TName>["rules"];
  defaultValue?: ControllerProps<TFieldValues, TName>["defaultValue"];
  disabled?: InputNumberProps["disabled"];
  isRequired?: boolean;
  onChange?: InputNumberProps["onChange"];
  formItemProps?: Omit<FormItemProps, "name" | "label" | "children">;
  inputNumberProps?: InputNumberProps;
  format?: "number" | "currency";
  addonBefore?: React.ReactNode | (() => ReactNode);
  stringMode?: boolean;
};

export function InputNumberFieldV2<TFieldValues extends FieldValues, TName extends Path<TFieldValues>>(
  props: InputNumberFieldProps<TFieldValues, TName>
) {
  const {
    fieldname,
    label,
    control,
    rules,
    defaultValue,
    disabled,
    isRequired,
    onChange,
    formItemProps,
    inputNumberProps,
    format = "number",
    addonBefore,
    stringMode = true,
  } = props;

  const {
    precision: inputPrecision,
    onChange: _inputOnChange,
    style: inputStyle,
    ...restInputNumberProps
  } = inputNumberProps ?? {};
  const { extra, ...restFormItemProps } = formItemProps ?? {};

  const isFieldRequired = Boolean(isRequired || (rules && "required" in rules && rules.required));
  const effectiveRules = isRequired
    ? {
        ...rules,
        required: rules?.required ?? getRequiredErrorMessage(),
      }
    : rules;

  const precision = format === "currency" ? 2 : typeof inputPrecision === "number" ? inputPrecision : 0;

  const parser: InputNumberProps["parser"] = (value) => {
    if (!value) return "";

    const sanitized = value.replace(/[^\d.]/g, "");
    const [rawInteger = "", ...rawDecimals] = sanitized.split(".");

    if (precision === 0 || rawInteger?.length === 0) return rawInteger;

    const decimal = rawDecimals.join("").slice(0, precision);

    return decimal ? `${rawInteger}.${decimal}` : `${rawInteger}.`;
  };

  const formatter: InputNumberProps["formatter"] = (value) => {
    if (value == null || value === "") return "";

    const [rawInteger = "", rawDecimal] = String(value).split(".");

    const decimal = precision > 0 ? rawDecimal?.slice(0, precision) : undefined;
    const integer = format === "currency" ? Number(rawInteger).toLocaleString("en-US") : rawInteger;

    return decimal ? `${integer}.${decimal}` : integer;
  };
  const renderAddon = typeof addonBefore === "function" ? addonBefore() : addonBefore;
  return (
    <Controller
      name={fieldname}
      control={control}
      rules={effectiveRules}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => {
        const handleChange: InputNumberProps["onChange"] = (value) => {
          field.onChange(value);
          onChange?.(value);
        };

        return (
          <Form.Item
            {...restFormItemProps}
            label={label}
            required={isFieldRequired}
            validateStatus={fieldState.error ? "error" : undefined}
            help={fieldState.error?.message}
            extra={renderExtra(extra)}
          >
            <InputNumber
              {...restInputNumberProps}
              {...field}
              addonBefore={renderAddon || undefined}
              onChange={handleChange}
              disabled={disabled}
              precision={precision}
              stringMode={stringMode}
              controls={false}
              parser={parser}
              formatter={formatter}
              style={{ width: "100%", ...inputStyle }}
            />
          </Form.Item>
        );
      }}
    />
  );
}
